import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, TrendingUp, DollarSign, MousePointerClick, Users, Award, LogOut } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Authenticated affiliate dashboard — the "home page" for role='affiliate'
 * users, mirroring how clients see /dashboard and attorneys see /attorney.
 *
 * Route: /affiliate/dashboard (Protected, role='affiliate')
 */
export default function AffiliateDashboardPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/affiliate/me")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  const copy = async () => {
    if (!data?.affiliate?.share_url) return;
    const url = data.affiliate.share_url;
    // navigator.clipboard rejects in iframes/insecure contexts; fall back to
    // the classic hidden-textarea + document.execCommand('copy') technique.
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) {
        console.warn("[AffiliateDashboardPage] Clipboard copy failed:", err);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const dollars = (c) =>
    `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  if (err) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-20 text-center" data-testid="affiliate-dash-error">
          <h1 className="font-display text-2xl font-bold mb-2">We couldn't load your account</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{err}</p>
          <Link to="/" className="btn-primary mt-6 inline-block">Back to home</Link>
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-20 text-center text-sm"
             style={{ color: "var(--text-secondary)" }}>Loading your affiliate dashboard…</div>
      </AppShell>
    );
  }

  const a = data.affiliate;
  const s = data.stats;
  const conv = data.conversions || [];

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="affiliate-dashboard-page">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div>
            <div className="pill pill-ai inline-flex mb-2">
              <Award className="w-3.5 h-3.5 mr-1.5" />
              {a.status === "approved" ? "Active affiliate" : a.status}
            </div>
            <h1 className="font-display text-3xl font-extrabold">
              Welcome back, {a.name.split(" ")[0]}.
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              Your affiliate code:{" "}
              <code className="font-mono font-bold"
                style={{ background: "var(--ai-bg)", color: "var(--ai-text)",
                          padding: "2px 6px", borderRadius: 4 }}
                data-testid="affiliate-dash-code">
                {a.code}
              </code>
            </p>
          </div>
          <button onClick={logout} className="ml-auto btn-ghost text-sm inline-flex items-center gap-1"
                  data-testid="affiliate-dash-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        {/* Share URL */}
        <div className="card-surface p-5 mb-6" data-testid="affiliate-dash-share-block">
          <div className="text-xs font-semibold uppercase tracking-wide mb-2"
               style={{ color: "var(--text-secondary)" }}>Your share link</div>
          <div className="flex items-center gap-2">
            <input className="field-input font-mono text-sm" readOnly value={a.share_url}
                   data-testid="affiliate-dash-share-url" />
            <button type="button" className="btn-primary text-sm inline-flex items-center gap-1"
                    onClick={copy} data-testid="affiliate-dash-copy-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
            Post this on TikTok, YouTube, WeChat, or wherever your audience is. Every primary
            DS-260 paid case that comes through your link earns you a commission.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat icon={<MousePointerClick className="w-4 h-4" />} label="Clicks"
                value={s.clicks} testId="affiliate-dash-stat-clicks" />
          <Stat icon={<Users className="w-4 h-4" />} label="Signups"
                value={s.signups} testId="affiliate-dash-stat-signups" />
          <Stat icon={<TrendingUp className="w-4 h-4" />} label="Conversions"
                value={(s.paid_conversions || 0) + (s.pending_conversions || 0)}
                testId="affiliate-dash-stat-conversions" />
          <Stat icon={<DollarSign className="w-4 h-4" />} label="Total earnings"
                value={dollars(s.earnings_total_cents)} testId="affiliate-dash-stat-total" />
        </div>

        {/* Earnings breakdown */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <EarnBox label="Pending (owed to you)" cents={s.earnings_pending_cents}
                   count={s.pending_conversions} tone="pending"
                   testId="affiliate-dash-earn-pending" />
          <EarnBox label="Paid out to you" cents={s.earnings_paid_cents}
                   count={s.paid_conversions} tone="paid"
                   testId="affiliate-dash-earn-paid" />
        </div>

        {/* Conversions list */}
        <div className="card-surface p-5" data-testid="affiliate-dash-conversions">
          <div className="font-display font-bold text-base mb-3">Recent conversions</div>
          {conv.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Nothing yet — once someone signs up via your link and pays for a primary
              DS-260 case, you'll see them here.
            </p>
          ) : (
            <div className="space-y-1">
              {conv.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-xs p-2 rounded"
                     style={{ background: "var(--bg-cream)" }}
                     data-testid={`affiliate-dash-conv-${c.id}`}>
                  <span className="font-mono">Case #{c.case_id?.slice(0, 8)}</span>
                  <span>{c.created_at?.slice(0, 10)}</span>
                  <span className="font-bold">{dollars(c.payout_cents)}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{
                          background: c.payout_status === "paid" ? "#D1FAE5" :
                                      c.payout_status === "void" ? "#E5E7EB" : "#FEF3C7",
                          color: c.payout_status === "paid" ? "#065F46" :
                                 c.payout_status === "void" ? "#374151" : "#92400E",
                        }}>
                    {c.payout_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs mt-6" style={{ color: "var(--text-secondary)" }}>
          Payouts are sent to <strong>{a.payout_method || "your preferred method"}</strong>
          {a.payout_email && <> ({a.payout_email})</>}. Update your payout details or
          request a new share asset? Email <a href="mailto:support@ds-260usa.com"
            className="underline">support@ds-260usa.com</a>.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div className="card-surface p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>
        {icon} {label}
      </div>
      <div className="font-display text-2xl font-extrabold"
           style={{ color: "var(--text-primary)" }}>{value}</div>
    </div>
  );
}

function EarnBox({ label, cents, count, tone, testId }) {
  const isPaid = tone === "paid";
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="font-display text-3xl font-extrabold"
           style={{ color: isPaid ? "#065F46" : "var(--accent-copper)" }}>
        ${((cents || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}
      </div>
      <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
        {count} {count === 1 ? "conversion" : "conversions"}
      </div>
    </div>
  );
}
